// groupPrivateHandler.js
'use strict';
import {
  MERGE_LIVE_STREAM_DATA,
  MERGE_OPERATION_DATA,
  REMOVE_OPERATION_DATA,
} from '../ActionTypes.js';

import getOperationData from '../selector/getOperationData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getGoalData from '../selector/getGoalData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';

import addModal from '../action/addModal.js';
import setPrivateStreamAutoPayment from '../action/setPrivateStreamAutoPayment.js';
import setIsPrivateStreamUnlocked from '../action/setIsPrivateStreamUnlocked.js';
import setTriggerPrivateInviteCountdown from '../action/setTriggerPrivateInviteCountdown.js';

import {
  GroupPrivateStatus,
  LivestreamTriggerType,
  PRESET_PREVIEW,
} from '../resource/liveStreamConstants.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';

/**
 * Group private handler
 * @kind action
 * @param {string} {streamId} - stream id.
 * @return {Promise} Action promise.
 */
const groupPrivateHandler =
  ({ streamId } = {}) =>
  async (dispatch, getState) => {
    const currentGoalId = getLiveStreamData(
      getState(),
      [streamId, 'groupPrivate'],
      'currentId'
    );
    const lastGoalId = getLiveStreamData(
      getState(),
      [streamId, 'groupPrivate'],
      'lastId'
    );
    const status = getLiveStreamData(
      getState(),
      [streamId, 'groupPrivate'],
      'status'
    );

    const isConsidering = !!currentGoalId;

    if (isConsidering) {
      if (GroupPrivateStatus.CONSIDERING === status) {
        return;
      }
      return dispatch(toConsidering({ streamId }));
    }

    const isPreview =
      PRESET_PREVIEW === getLiveStreamData(getState(), [streamId], 'status');
    const agreedUnix = getGoalData(getState(), [lastGoalId], 'agreed');
    const isAccepted = isPreview && agreedUnix != null;

    if (isAccepted) {
      if (GroupPrivateStatus.ACCEPTED === status) {
        return;
      }
      return dispatch(toAccepted({ streamId }));
    }

    const cooldownUnix = getGoalData(getState(), [lastGoalId], 'cooldown');

    const nowUnix = getCurrentUnixTimestamp({
      offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
    });

    const isInCooldown = cooldownUnix != null && cooldownUnix > nowUnix;

    if (isInCooldown) {
      if (GroupPrivateStatus.COOLDOWN === status) {
        return;
      }
      return dispatch(toCooldown({ streamId }));
    }

    if (GroupPrivateStatus.DEFAULT !== status) {
      return dispatch(toDefault({ streamId }));
    }
  };

export default groupPrivateHandler;

/**
 * Set group private status to default
 * @kind action
 * @param {string} {streamId} - stream id.
 * @return {Promise} Action promise.
 */
const toDefault =
  ({ streamId } = {}) =>
  async dispatch => {
    dispatch(setToDefaultStatusTimer({ streamId, timerUnixTimestamp: null }));
    dispatch({
      type: MERGE_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'groupPrivate'],
        data: {
          status: GroupPrivateStatus.DEFAULT,
          currentId: undefined,
          lastId: undefined,
        },
      },
    });
  };

/**
 * Set group private status to considering
 * @kind action
 * @param {string} {streamId} - stream id.
 * @return {Promise} Action promise.
 */
const toConsidering =
  ({ streamId } = {}) =>
  async (dispatch, getState) => {
    dispatch(setToDefaultStatusTimer({ streamId, timerUnixTimestamp: null }));
    dispatch({
      type: MERGE_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'groupPrivate'],
        data: {
          status: GroupPrivateStatus.CONSIDERING,
        },
      },
    });

    const inviteTriggerType = getLiveStreamData(
      getState(),
      [streamId, 'triggerPrivateInvite'],
      'triggerType'
    );
    const isInviteSubmitted = getLiveStreamData(
      getState(),
      [streamId, 'triggerPrivateInvite'],
      'isInviteSubmitted'
    );
    // clear countdown only when request has been send and user's own triggerType === TRIGGER_PRIVATE
    if (
      LivestreamTriggerType.TRIGGER_PRIVATE === inviteTriggerType &&
      isInviteSubmitted
    ) {
      dispatch(setTriggerPrivateInviteCountdown({ streamId, seconds: null }));
    }
  };

/**
 * Set group private status to accepted
 * @kind action
 * @param {string} {streamId} - stream id.
 * @return {Promise} Action promise.
 */
const toAccepted =
  ({ streamId } = {}) =>
  async (dispatch, getState) => {
    dispatch(setToDefaultStatusTimer({ streamId, timerUnixTimestamp: null }));
    dispatch({
      type: MERGE_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'groupPrivate'],
        data: {
          status: GroupPrivateStatus.ACCEPTED,
        },
      },
    });
    const isPrivateStreamUnlocked = getOperationData(
      getState(),
      ['liveStream', 'paid', streamId],
      'isPrivateStreamUnlocked'
    );
    // Creator accept the request which user joined/created.
    if (isPrivateStreamUnlocked) {
      dispatch(
        addModal({
          id: 'ModalTriggerPrivateSucceeded',
          transitionStatus: 'opening',
        })
      );
    }
  };

/**
 * Set group private status to cooldown
 * @kind action
 * @param {string} {streamId} - stream id.
 * @return {Promise} Action promise.
 */
const toCooldown =
  ({ streamId } = {}) =>
  async (dispatch, getState) => {
    const isPrivateStreamUnlocked = getOperationData(
      getState(),
      ['liveStream', 'paid', streamId],
      'isPrivateStreamUnlocked'
    );

    dispatch(setPrivateStreamAutoPayment({ streamId, isEnabled: false }));
    dispatch(
      setIsPrivateStreamUnlocked({ streamId, isPrivateStreamUnlocked: false })
    );

    // Creator decline the request which user joined/created.
    if (isPrivateStreamUnlocked) {
      dispatch(
        addModal({
          id: 'GroupPrivateDeclinedModal',
          transitionStatus: 'opening',
        })
      );
    }

    const lastGoalId = getLiveStreamData(
      getState(),
      [streamId, 'groupPrivate'],
      'lastId'
    );
    const cooldownUnix = getGoalData(getState(), [lastGoalId], 'cooldown');

    dispatch(
      setToDefaultStatusTimer({
        streamId,
        timerUnixTimestamp: cooldownUnix,
      })
    );
    dispatch({
      type: MERGE_LIVE_STREAM_DATA,
      payload: {
        selectPath: [streamId, 'groupPrivate'],
        data: {
          status: GroupPrivateStatus.COOLDOWN,
        },
      },
    });
  };

/**
 * Set to default status timer and remove current one.
 * @kind action
 * @param {string} {streamId} - stream id.
 * @param {number} {timerUnixTimestamp} - timer unix timestamp.
 * @return {Promise} Action promise.
 */
const setToDefaultStatusTimer =
  ({ streamId, timerUnixTimestamp } = {}) =>
  async (dispatch, getState) => {
    const selectPath = [
      'timer',
      'livestream',
      streamId,
      'groupPrivateToDefault',
    ];

    let timerId = getOperationData(getState(), selectPath, 'timerId');

    if (timerId) {
      clearTimeout(timerId);
      dispatch({
        type: REMOVE_OPERATION_DATA,
        payload: {
          selectPath,
        },
      });
    }

    if (timerUnixTimestamp) {
      const nowUnix = getCurrentUnixTimestamp({
        offsetSeconds: getTimestampOffset(getState(), Accuracy.SECOND),
      });
      timerId = setTimeout(
        () => {
          dispatch(groupPrivateHandler({ streamId }));
        },
        (timerUnixTimestamp - nowUnix) * 1000
      );
      dispatch({
        type: MERGE_OPERATION_DATA,
        payload: {
          selectPath,
          data: {
            timerId,
            unixTimestamp: timerUnixTimestamp,
          },
        },
      });
    }
  };
